import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

const THEME_STORAGE_KEY = "betto-theme";
const DEFAULT_THEME = "dark";

const ThemeContext = createContext({
	theme: DEFAULT_THEME,
	toggleTheme: () => {},
	setTheme: () => {},
	isDark: true,
});

function ThemeProvider({ children }) {
	const isBetto = window.TENANT === "betto";

	const [theme, setThemeState] = useState(() => {
		if (!isBetto) return DEFAULT_THEME;
		try {
			const saved = localStorage.getItem(THEME_STORAGE_KEY);
			if (saved === "light" || saved === "dark") return saved;
		} catch (e) {
			console.error(e);
		}
		return DEFAULT_THEME;
	});

	const applyTheme = useCallback((newTheme) => {
		document.documentElement.setAttribute("data-theme", newTheme);
		document.documentElement.classList.remove("theme-dark", "theme-light");
		document.documentElement.classList.add(`theme-${newTheme}`);
	}, []);

	useEffect(() => {
		applyTheme(theme);
		if (isBetto) {
			try {
				localStorage.setItem(THEME_STORAGE_KEY, theme);
			} catch (e) {
				console.error(e);
			}
		}
	}, [theme, applyTheme, isBetto]);

	const toggleTheme = useCallback(() => {
		if (!isBetto) return;
		setThemeState((prev) => (prev === "dark" ? "light" : "dark"));
	}, [isBetto]);

	const setTheme = useCallback(
		(newTheme) => {
			if (!isBetto) return;
			if (newTheme === "dark" || newTheme === "light") {
				setThemeState(newTheme);
			}
		},
		[isBetto]
	);

	const value = {
		theme,
		toggleTheme,
		setTheme,
		isDark: theme === "dark",
		isLight: theme === "light",
		isBetto,
	};

	return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

function useTheme() {
	const context = useContext(ThemeContext);
	if (!context) {
		throw new Error("useTheme must be used within a ThemeProvider");
	}
	return context;
}

export { ThemeContext, ThemeProvider, useTheme };
